import PageLoading from '@/components/PageLoading';
import PagePlaceholder from '@/components/PagePlaceholder';
import RouteFailure from '@/components/RouteFailure';
import Forbidden from '@/pages/403';
import type { ComponentType } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
} from 'react-router';
import AuthGuard from './AuthGuard';
import { currentBasename } from './basename';
import { registerRouteTree } from './preload';

/**
 * 页面懒加载（按页分包）
 *
 * 用 react-router 的 route-level lazy 而不是 React.lazy + Suspense：
 * 导航时会先 await 这个 chunk、加载完才提交这次导航并渲染，
 * 所以不会出现"URL 已经变了、组件还没到"的空窗，也就不需要 loading 占位。
 * （这正是 vue-router 处理异步组件的方式）
 *
 * 同一件事的另一面：**点下去才开始下载**。到源站 RTT 220ms 的情况下，一页首访里
 * 约 870ms 花在「拉 chunk + 执行 + 首次渲染」上（2026-10-22 实测 71 页中位数）。
 * 所以把 `factory` 另外挂到 `prefetch` 上，由菜单悬停调 `preloadRoute(path)` 提前拉；
 * 预取只是提前量，失败不影响导航（见 `router/preload.ts`）。
 */
const load = (factory: () => Promise<{ default: ComponentType<any> }>) =>
  Object.assign(async () => ({ Component: (await factory()).default }), {
    prefetch: () => factory(),
  });

/**
 * 「页面写完了，在等后端接口」的路由占位
 *
 * mock 退役（`docs/superpowers/specs/2026-09-02-mock-retirement-design.md`）后，
 * 后端还没有对应接口的页面**不再被路由 import**——不 import 就不发请求，也就不会
 * 报错，而不是让用户点进去看一屏 502。
 *
 * 🔴 **页面文件原封不动留在原地**，一行没删。接上后端时把这里换回
 * `lazy: load(() => import('@/pages/...'))` 即可，改一行就复活。之所以停在路由
 * 这一层而不是改页面文件：页面里那些表格、筛选、弹窗都是已经做完并验收过的，
 * 换成占位等于把它们扔掉；也不停在 service 层——那要给 113 个函数各编一份空返回值，
 * 而 `counts: {live:0,off:0,all:0}` 这种空值会在界面上显示成「在会 0 人」，
 * 那是**假陈述**，比假数据更难发现（三条禁令的第二条）。
 *
 * `apis` 列的是该页在等哪些接口，直接给后端同事看。
 */
const pendingApi = (code: string, apis: string[]) => (
  <PagePlaceholder code={code} pending="api" apis={apis} />
);

/**
 * 总会后台端（总台）路由表
 *
 * - 路由是静态的，菜单由服务端 `reachableRoutes` 译成的页面键（`useAuthStore().menus`）与前端
 *   `src/constants/menuCatalog.ts` 求交集动态生成（超管取全表）；每个页面用 handle.access
 *   声明 PRD 路由索引里的权限键，由 BasicLayout 统一校验（`canEnter`）。
 *   子页另有 handle.actionAccess：独立设置包授的是动作码，进不了 `menus`，由 BasicLayout
 *   用 `can` 对服务端下发的动作码判；两档满足其一即可进。
 * - 只有两级：一级模块 + 二级页面。原型侧边栏里的"分组"（人员管理 / 会员管理…）
 *   只是菜单分组标题，不占路由层级。
 * - 二三期模块（训练 / 课程 / 教务 / 商品 / 数据）本期不做，不下发也不留路由。
 * - 属会受限后台是同一套代码、不同登录入口，后续挂在 /club 下（见 README）。
 *
 * 🔴 以下路由**刻意不挂 `handle.access`**（页面能进，但页面里的接口由服务端逐条判：
 * 网关没放行的调用一律 403，还没迁到微服务的则在 `utils/http.ts` 的出口被拒）。
 * 这是有意的，不是漏改，
 * 理由见 `docs/前端鉴权与权限模型对接设计.md` §5.3：
 * - 一级分组节点（`acct` / `member` / `org` / `ops` / `order` / `fin` / `sys`）：
 *   只是路由分组，不是页面，没有对应的权限键
 * - `/home`：PRD 明文「工作台不单独授权」
 * - 占位路由（`pendingApi`）：页面本身还没接后端，`pendingApi` 的第一个参数只用来取
 *   页面标题（词条 `menu.<code>`）；接上后端后照常挂 `handle.access`。
 *   `order.create` 仍是这类占位页，且登记表没有页面码，侧栏对所有人都不出。
 *
 * `/order/person`、`/order/club`、`/order/dunning` 已按 `ALWAYS_VISIBLE_MENU` 的菜单键
 * 挂上 `handle.access`：能不能进由 iam 的 `AdminLandingPage` 是否把这些路由算进
 * `reachableRoutes` 决定，菜单过滤与直开 URL 用同一份判据。
 */
const pageRoutes: RouteObject[] = [
  { path: '/login', lazy: load(() => import('@/pages/Login')) },
  // 🔴 密码几条路的落地页全部与 /login 平级、**不在 AuthGuard 里**。
  //
  // - `/password/change`：PRD 里它是独立的一页（不带侧边栏那套布局），顶栏用户菜单进。
  //   `/change-password` 是它的旧地址，留着免得旧链接 404。
  //   注意：这一页的接口还是旧单体的，现在提交会明确报错、不出网（见 services/auth.ts 文件头）。
  // - `/password/forgot` 与两条重置路径：服务端把它们登记在 `AnonymousEndpoints#PATHS`
  //   里，本来就是匿名端点——用户是**登不进来**才走这条路，进不了守卫。
  //   （同样是旧单体的接口，同上。）
  {
    path: '/password/change',
    lazy: load(() => import('@/pages/Password/Change')),
  },
  {
    path: '/change-password',
    lazy: load(() => import('@/pages/Password/Change')),
  },
  {
    path: '/password/forgot',
    lazy: load(() => import('@/pages/Password/Forgot')),
  },
  // 🔴 重置页刻意挂两条路径，指向同一个组件。`/reset-password` 是服务端配置
  // `muou.auth.reset-link.admin-base-url` 的默认值（`http://localhost:5173/reset-password`）
  // 指向的那一条，`/password/reset` 是与另两页同一命名家族的那一条。
  // 前端猜错部署侧怎么配的表现不是报错，是**用户收到一封点开就 404 的邮件**——
  // 两条都挂则两种配法都能用。详见 `pages/Password/Reset.tsx` 的类注释。
  {
    path: '/reset-password',
    lazy: load(() => import('@/pages/Password/Reset')),
  },
  {
    path: '/password/reset',
    lazy: load(() => import('@/pages/Password/Reset')),
  },
  { path: '/no-post', lazy: load(() => import('@/pages/NoPost')) },
  // 邮件里那条设置密码链接的落点（帐号服务 iam 发的）：这时人还没登录（新开号的更是一个密码都没有），
  // 所以与 /login 同级、在 AuthGuard 之外，凭据是 URL 上的一次性令牌
  { path: '/set-password', lazy: load(() => import('@/pages/SetPassword')) },
  {
    path: '/',
    element: <AuthGuard />,
    // 首屏还在加载首个页面 chunk 时的兜底（之后的导航靠 lazy，不再有占位）
    HydrateFallback: PageLoading,
    children: [
      { index: true, element: <Navigate to="/home" replace /> },
      {
        path: 'home',
        lazy: load(() => import('@/pages/Home/Home')),
      },
      {
        // ⚠️ 临时：验收稿对照页，验收完成后连同这个路由一起删除
        path: 'spec-preview',
        lazy: load(() => import('@/pages/SpecPreview')),
      },
      {
        // 帐号中心
        path: 'acct',
        children: [
          { index: true, element: <Navigate to="/acct/portal" replace /> },
          {
            path: 'portal',
            lazy: load(() => import('@/pages/muou/acct/account-list/Portal')),
            handle: { access: 'acct.portal' },
          },
          {
            path: 'portal/change-log',
            lazy: load(
              () => import('@/pages/muou/acct/account-list/AccountChangeLog'),
            ),
            handle: {
              access: 'acct.portal',
              breadcrumbTitle: 'acct.portal.action.changeLog',
            },
          },
          {
            path: 'access',
            lazy: load(() => import('@/pages/muou/acct/access/Access')),
            handle: { access: 'acct.access' },
          },
          {
            path: 'req',
            lazy: load(
              () => import('@/pages/muou/acct/request-queue/RequestQueue'),
            ),
            handle: { access: 'acct.req' },
          },
          {
            path: 'policy',
            lazy: load(() => import('@/pages/muou/acct/policy/Policy')),
            handle: { access: 'acct.policy' },
          },
        ],
      },
      {
        // 会员中心
        path: 'member',
        children: [
          {
            index: true,
            element: <Navigate to="/member/person-list" replace />,
          },
          {
            path: 'person-list',
            lazy: load(() => import('@/pages/muou/member/PersonList')),
            handle: { access: 'member.person.list' },
          },
          {
            path: 'person-archive',
            lazy: load(() => import('@/pages/muou/member/PersonArchive')),
            handle: { access: 'member.person.archive' },
          },
          {
            // 子页权限：有人员档案页权限、或独立动作码，二者有其一即可进。
            // 详情页不占侧栏入口（需要 psn，从档案列表/人员列表进入）
            path: 'person-arch',
            lazy: load(() => import('@/pages/muou/member/PersonArchiveDetail')),
            handle: {
              access: 'member.person.archive',
              actionAccess: 'member.person.arch',
            },
          },
          {
            path: 'person-audit',
            lazy: load(() => import('@/pages/muou/member/PersonAudit')),
            handle: { access: 'member.person.audit' },
          },
          {
            path: 'person-corr',
            lazy: load(() => import('@/pages/muou/member/PersonCorrection')),
            handle: { access: 'member.person.corr' },
          },
          {
            // 🔴 TODO(server): `member.list` 这个键**服务端自己的 label 与 apis 对不上**，
            // 前端这条映射因此必然有一半是错的，两种改法都可能是错的，所以先不动。
            //
            // 服务端 `PermRegistry.MENUS` 里 `member.list` 的 label 写「会员列表」，
            // 而它门控的 apis 是 `/api/v1/tenant/persons*` + `/person-archives*`
            // （自然人 / 人员档案），跟「会员」不是一回事。
            //
            // 后果（现在就在发生）：被授权 `member.list` 的岗，侧边栏看到的是「会员列表」，
            // 点进来这一页（`MemberList.tsx`）调的是 `/admin/member/identity/pageMembers`——**不在任何菜单键的
            // api 集合里**，接真后端后会被 `PermissionInterceptor` 按「未登记路由一律不放行」
            // 判 403；而真正该他看的人员页 `/member/person-list`（`PersonList.tsx`，调
            // persons）没有任何菜单键，侧边栏里根本没有入口。
            //
            // 等服务端把 label 与 apis 二选一改掉（改 label 叫「人员档案」→ menuKey 挪到
            // person-list；改 apis 指向会员接口 → 留在本页）之后再调整这一行。
            path: 'list',
            lazy: load(() => import('@/pages/muou/member/MemberList')),
            handle: { access: 'member.list' },
          },
          {
            path: 'join-audit',
            lazy: load(() => import('@/pages/muou/member/JoinAudit')),
            handle: { access: 'member.audit.join' },
          },
          {
            path: 'renew-audit',
            lazy: load(() => import('@/pages/muou/member/RenewAudit')),
            handle: { access: 'member.audit.renew' },
          },
          {
            // 本模块设置是会员类型页的子页（PRD 从页内链接进入，不占侧栏）
            path: 'type/settings',
            lazy: load(() => import('@/pages/muou/member/MemberTypeSettings')),
            handle: {
              access: 'member.type',
              // 两个包各自都能进：只读设置包（member.type.set）与只管保存的包（member.membership.setting）
              actionAccess: ['member.type.set', 'member.membership.setting'],
            },
          },
          {
            path: 'type',
            lazy: load(() => import('@/pages/muou/member/MemberType')),
            handle: { access: 'member.type' },
          },
          {
            path: 'qual-list',
            lazy: load(() => import('@/pages/muou/member/QualList')),
            handle: { access: 'member.qual.list' },
          },
          {
            path: 'qual-audit',
            lazy: load(() => import('@/pages/muou/member/QualAudit')),
            handle: { access: 'member.qual.audit' },
          },
          {
            path: 'qual-renew',
            lazy: load(() => import('@/pages/muou/member/QualRenew')),
            handle: { access: 'member.qual.renew' },
          },
          {
            path: 'qual-clubjoin',
            lazy: load(() => import('@/pages/muou/member/QualClubJoinAudit')),
            handle: { access: 'member.qual.clubjoin' },
          },
          {
            path: 'qual-transfer',
            lazy: load(() => import('@/pages/muou/member/QualTransfer')),
            handle: { access: 'member.qual.transfer' },
          },
          {
            path: 'qual-type/settings',
            lazy: load(() => import('@/pages/muou/member/QualTypeSettings')),
            handle: {
              access: 'member.qual.type',
              actionAccess: 'member.qual.type.set',
            },
          },
          {
            path: 'qual-type',
            lazy: load(() => import('@/pages/muou/member/QualType')),
            // 本页已接真接口（`services/muou-qual.ts` 的 `QUAL_TYPES =
            // '/v1/tenant/qual-types'`，提交 70a107c）。此处原有一段「仍整页挂 mock」
            // 的注释来自更早的 4e1211c，已随那次接口对接失效，2026-09-02 合并时删除。
            handle: { access: 'member.qual.type' },
          },
          {
            path: 'club-list',
            lazy: load(() => import('@/pages/muou/member/club-list/ClubList')),
            handle: { access: 'member.club.list' },
          },
          {
            path: 'club-audit',
            lazy: load(
              () => import('@/pages/muou/member/club-audit/ClubAudit'),
            ),
            handle: { access: 'member.club.audit' },
          },
          {
            path: 'club-upgrade',
            lazy: load(
              () => import('@/pages/muou/member/club-upgrade/ClubUpgrade'),
            ),
            handle: { access: 'member.club.upgrade' },
          },
          {
            path: 'club-score',
            lazy: load(
              () => import('@/pages/muou/member/club-score/ClubScore'),
            ),
            handle: { access: 'member.club.score' },
          },
          {
            path: 'club-level/settings',
            lazy: load(
              () =>
                import(
                  '@/pages/muou/member/club-level/settings/ClubEffectSettings'
                ),
            ),
            handle: {
              access: 'member.club.level',
              actionAccess: 'member.club.level.set',
              breadcrumbTitle: 'club.level.page.settings',
            },
          },
          {
            path: 'club-level/kinds',
            lazy: load(
              () =>
                import(
                  '@/pages/muou/member/club-level/reference/ClubLevelReferencePage'
                ),
            ),
            handle: {
              access: 'member.club.level',
              breadcrumbTitle: 'club.level.page.kinds',
            },
          },
          {
            path: 'club-level/metrics',
            lazy: load(
              () =>
                import(
                  '@/pages/muou/member/club-level/reference/ClubLevelReferencePage'
                ),
            ),
            handle: {
              access: 'member.club.level',
              breadcrumbTitle: 'member.a.clubLevelRef.metricsTitle',
            },
          },
          {
            path: 'club-level',
            lazy: load(
              () => import('@/pages/muou/member/club-level/ClubLevel'),
            ),
            handle: { access: 'member.club.level' },
          },
          {
            path: 'cert-type',
            lazy: load(() => import('@/pages/muou/member/CertType')),
            handle: { access: 'cert.type' },
          },
          {
            path: 'cert-list',
            lazy: load(() => import('@/pages/muou/member/CertList')),
            handle: { access: 'cert.list' },
          },
          {
            path: 'cert-audit',
            lazy: load(() => import('@/pages/muou/member/CertAudit')),
            handle: { access: 'cert.audit' },
          },
        ],
      },
      {
        // 组织会务
        path: 'org',
        children: [
          { index: true, element: <Navigate to="/org/notice" replace /> },
          {
            path: 'notice',
            lazy: load(() => import('@/pages/muou/org/Notice')),
            handle: { access: 'org.notice' },
          },
          {
            path: 'meeting',
            lazy: load(() => import('@/pages/muou/org/Meeting')),
            handle: { access: 'org.meeting' },
          },
          {
            path: 'motion',
            lazy: load(() => import('@/pages/muou/org/Motion')),
            handle: { access: 'org.motion' },
          },
          {
            path: 'todo',
            lazy: load(() => import('@/pages/muou/org/Todo')),
            handle: { access: 'org.todo' },
          },
          {
            path: 'info',
            lazy: load(() => import('@/pages/muou/org/Info')),
            handle: { access: 'org.info' },
          },
          {
            path: 'dept',
            lazy: load(() => import('@/pages/muou/org/Dept')),
            handle: { access: 'org.dept' },
          },
          {
            path: 'job',
            lazy: load(() => import('@/pages/muou/org/Job')),
            handle: { access: 'org.job' },
          },
          {
            path: 'cmt',
            lazy: load(() => import('@/pages/muou/org/CmtTree')),
            handle: { access: 'org.cmt' },
          },
          {
            path: 'committee',
            lazy: load(() => import('@/pages/muou/org/Committee')),
            handle: { access: 'org.committee' },
          },
          {
            path: 'cmtkind',
            lazy: load(() => import('@/pages/muou/org/CmtKind')),
            handle: { access: 'org.cmtkind' },
          },
          {
            path: 'duty',
            lazy: load(() => import('@/pages/muou/org/Duty')),
            handle: { access: 'org.duty' },
          },
          {
            // 职员帐号（org.staff）：页面码是 iam.employee.view，与后台授权共用一个页面码。
            // 侧边栏那一条是常显的（见 menuCatalog 的 ALWAYS_VISIBLE_MENU：藏起来等于把
            // PRD 的 B02/B03 两个建号入口一起藏掉），但直开 URL 仍按菜单键落 403。
            path: 'staff',
            lazy: load(() => import('@/pages/muou/org/Staff')),
            handle: { access: 'org.staff' },
          },
        ],
      },
      {
        // 运营配置
        path: 'ops',
        children: [
          {
            index: true,
            element: <Navigate to="/ops/site-template" replace />,
          },
          {
            path: 'site-template',
            lazy: load(
              () => import('@/pages/muou/ops/site-template/SiteTemplate'),
            ),
            handle: { access: 'ops.site.tpl' },
          },
          {
            path: 'site-config',
            lazy: load(() => import('@/pages/muou/ops/site-config/SiteConfig')),
            handle: { access: 'ops.site.cfg' },
          },
          {
            path: 'site-home',
            lazy: load(() => import('@/pages/muou/ops/site-home/SiteHome')),
            handle: { access: 'ops.site.home' },
          },
          {
            // 2026-08-19 新增：官网配置七页里唯一连占位都没有的一页
            path: 'site-page',
            lazy: load(() => import('@/pages/muou/ops/site-page/SitePage')),
            handle: { access: 'ops.site.page' },
          },
          {
            path: 'site-menu',
            lazy: load(() => import('@/pages/muou/ops/site-menu/SiteMenu')),
            handle: { access: 'ops.site.menu' },
          },
          {
            path: 'site-article',
            lazy: load(
              () => import('@/pages/muou/ops/site-article/SiteArticle'),
            ),
            handle: { access: 'ops.site.article' },
          },
          // 消息四页已接正式通知服务，服务端按唯一菜单目录登记 ops.msg.* 页面码/菜单键；
          // 发送起草台与群发任务共用同一张 bulk_task，页面码都取 ops.msg.bulk.view。
          {
            // 发送消息起草台：与群发任务同一张 bulk_task 的两个视角
            path: 'msg-send',
            lazy: load(() => import('@/pages/muou/ops/msg-send/MsgSend')),
            handle: { access: 'ops.msg.send' },
          },
          {
            path: 'msg-template',
            lazy: load(
              () => import('@/pages/muou/ops/message-template/MsgTemplate'),
            ),
            handle: { access: 'ops.msg.tpl' },
          },
          {
            path: 'msg-kind',
            lazy: load(() => import('@/pages/muou/ops/notify-kind/MsgKind')),
            handle: { access: 'ops.msg.kind' },
          },
          {
            path: 'msg-bulk',
            lazy: load(() => import('@/pages/muou/ops/msg-bulk/MsgBulk')),
            handle: { access: 'ops.msg.bulk' },
          },
          {
            path: 'msg-log',
            lazy: load(() => import('@/pages/muou/ops/msg-log/MsgLog')),
            handle: { access: 'ops.msg.log' },
          },
          {
            path: 'msg-group',
            lazy: load(() => import('@/pages/muou/ops/msg-group/MsgGroup')),
            handle: { access: 'ops.msg.group' },
          },
          {
            // 2026-08-24 新增：其他配置的第一页，原型 ops.field。
            // 顺序照原型侧栏「字段库 → 表单配置」，字段库是上游（模板里的字段全部来自它）
            path: 'field',
            lazy: load(() => import('@/pages/muou/ops/field-library')),
            handle: { access: 'ops.field' },
          },
          {
            path: 'asset',
            lazy: load(() => import('@/pages/muou/ops/asset/Asset')),
            handle: { access: 'ops.asset' },
          },
          {
            path: 'form',
            lazy: load(() => import('@/pages/muou/ops/form-settings/OpsForm')),
            handle: { access: 'ops.form' },
          },
          {
            path: 'form/edit/:formCode',
            lazy: load(() => import('@/pages/muou/ops/form-settings/Edit')),
            handle: { access: 'ops.form' },
          },
        ],
      },
      {
        // 订单中心
        path: 'order',
        children: [
          { index: true, element: <Navigate to="/order/person" replace /> },
          {
            path: 'person',
            lazy: load(() => import('@/pages/muou/order/Person')),
            handle: { access: 'order.person' },
          },
          {
            path: 'club',
            lazy: load(() => import('@/pages/muou/order/Club')),
            handle: { access: 'order.club' },
          },
          {
            path: 'refund',
            lazy: load(() => import('@/pages/muou/fin/Refund')),
            handle: { access: 'order.refund' },
          },
          {
            path: 'create',
            element: pendingApi('order.create', [
              'POST /muou/order',
              'GET /muou/order/type',
              'GET /muou/person',
              'GET /muou/club',
            ]),
          },
          {
            path: 'dunning',
            lazy: load(() => import('@/pages/muou/order/Dunning')),
            handle: { access: 'order.dunning' },
          },
        ],
      },
      {
        // 财务中心
        path: 'fin',
        children: [
          { index: true, element: <Navigate to="/fin/offline" replace /> },
          {
            path: 'offline',
            lazy: load(() => import('@/pages/muou/fin/Offline')),
            handle: { access: 'fin.offline' },
          },
          {
            path: 'refund',
            lazy: load(() => import('@/pages/muou/fin/Refund')),
            handle: { access: 'fin.refund' },
          },
          {
            path: 'fee',
            lazy: load(() => import('@/pages/muou/fin/Fee')),
            handle: { access: 'fin.fee' },
          },
          {
            path: 'transaction',
            lazy: load(() => import('@/pages/muou/fin/Transaction')),
            handle: { access: 'fin.txn' },
          },
          {
            path: 'statistics',
            lazy: load(() => import('@/pages/muou/fin/Statistics')),
            handle: { access: 'fin.stat' },
          },
          {
            path: 'rcp',
            lazy: load(() => import('@/pages/muou/fin/Receipt')),
            handle: { access: 'fin.rcp' },
          },
          {
            // 应收账款（fin.ar）：四视角台账、指标带与账龄，读 billing 应收段
            path: 'ar',
            lazy: load(() => import('@/pages/muou/fin/Receivable')),
            handle: { access: 'fin.ar' },
          },
        ],
      },
      {
        // 设置中心
        path: 'sys',
        children: [
          { index: true, element: <Navigate to="/sys/plan" replace /> },
          {
            path: 'plan',
            lazy: load(() => import('@/pages/muou/sys/Plan')),
            handle: { access: 'sys.plan' },
          },
          {
            path: 'business-presets',
            lazy: load(() => import('@/pages/muou/sys/BusinessPresets')),
            handle: { access: 'sys.businessPresets' },
          },
          {
            path: 'payment',
            lazy: load(() => import('@/pages/muou/sys/Payment')),
            handle: { access: 'sys.pay' },
          },
          {
            // 交易设置（sys.trade）：关单期限、线下审核时限、催缴节奏、补收期、账龄分档、开单与结算
            path: 'trade',
            lazy: load(() => import('@/pages/muou/sys/TradeConfig')),
            handle: { access: 'sys.trade' },
          },
          {
            // 发票管理（sys.invoice）：本页配的是收据怎么开，台账在 /fin/rcp
            path: 'invoice',
            lazy: load(() => import('@/pages/muou/sys/InvoiceConfig')),
            handle: { access: 'sys.invoice' },
          },
          {
            path: 'dict',
            lazy: load(() => import('@/pages/muou/sys/basic-dict/Dict')),
            handle: { access: 'sys.dict' },
          },
          {
            path: 'i18n',
            lazy: load(() => import('@/pages/muou/sys/i18n/SysI18n')),
            handle: { access: 'sys.i18n' },
          },
          {
            // 2026-08-25 新增：原型 sys.notify，此前连占位都没有
            path: 'notify',
            lazy: load(() => import('@/pages/muou/sys/notify/Notify')),
            handle: { access: 'sys.notify' },
          },
          {
            // 后台帐号（sys.admin）：接口全走帐号服务（iam），见页内注释。文件仍放在 pages/pms/User
            // （脚手架那三页里只有它被改写成了真页面，角色 / 资源两页已随角色层删除）
            path: 'admin',
            lazy: load(() => import('@/pages/pms/User')),
            handle: { access: 'sys.admin' },
          },
          {
            // 后台授权（org.grant）：与 /org/staff 同一份列表与同一个开号抽屉，
            // 共用 iam.employee.view 页面码；直开 URL 与职员帐号一样按菜单键判。
            path: 'grant',
            lazy: load(() => import('@/pages/muou/org/Grant')),
            handle: { access: 'org.grant' },
          },
          {
            // 岗位权限（sys.role）：本会自建岗位 + 配权限包，临时帐号与职位从这里挑岗位
            path: 'role',
            lazy: load(() => import('@/pages/muou/sys/Role')),
            handle: { access: 'sys.role' },
          },
          {
            // 日志管理合并页：三张表一页三页签（PRD sys-系统设置-6）。页签本身按
            // 下面三个旧权限键逐个判，所以这条路由的准入是「任一即可」。
            path: 'log',
            lazy: load(() => import('@/pages/muou/sys/Log')),
            handle: {
              access: ['sys.log.op', 'sys.log.login', 'sys.log.err'],
            },
          },
          // 三个旧路由保留直达能力：落到合并页的对应页签，不删页面文件
          {
            path: 'log-login',
            element: <Navigate to="/sys/log?tab=login" replace />,
            handle: { access: 'sys.log.login' },
          },
          {
            path: 'log-operation',
            element: <Navigate to="/sys/log?tab=op" replace />,
            handle: { access: 'sys.log.op' },
          },
          {
            path: 'log-error',
            element: <Navigate to="/sys/log?tab=err" replace />,
            handle: { access: 'sys.log.err' },
          },
        ],
      },
      {
        // 权限包维护（sys.package）：菜单键归设置中心，路径按设计走 /iam/package；
        // 服务端 AdminLandingPage 用页面码 iam.package.view 把这条路由放进 reachableRoutes
        path: 'iam/package',
        lazy: load(() => import('@/pages/muou/sys/Package')),
        handle: { access: 'sys.package' },
      },
      // 2026-08-31 换鉴权模型：个人资料页已删。它整页读的是第一版 `CurrentUser`
      // （`/user/detail` + `/user` PATCH），服务端两个端点都不存在；自助档案接口没有之前
      // 补不回来。改密页在上面与 /login 平级（服务端 `/auth/password/change` 是真的）。
      // 外链内嵌打开
      { path: 'iframe', lazy: load(() => import('@/pages/Iframe')) },
      // 403 页在 BasicLayout 里也会直接用到，这里静态引入避免重复分包
      { path: '403', element: <Forbidden /> },
      { path: '*', lazy: load(() => import('@/pages/404')) },
    ],
  },
];

/**
 * 路由表外面再包一层，唯一目的是挂 `errorElement`。
 *
 * 挂在最外层才能连 `/login`、`/set-password` 这些与 `/` 平级的页面一起接住：
 * 页面 chunk 加载失败（部署换版后旧标签页的常态，见 `utils/chunkRecovery.ts`）
 * 或页面渲染抛错时，react-router 会一路往上找最近的 errorElement，没有就渲染它
 * 自带的英文 "Unexpected Application Error!" 页——那一页既没有出口，也不告诉用户
 * 该做什么。`RouteFailure` 换成一句人话加一个"重新载入"。
 */
export const routes: RouteObject[] = [
  {
    element: <Outlet />,
    errorElement: <RouteFailure />,
    children: pageRoutes,
  },
];

export const router = createBrowserRouter(routes, {
  basename: currentBasename() || undefined,
});

// 将合并后的完整路由树登记到页面预取表。
registerRouteTree(routes);
